/**
 * OIDC Callback Page
 * Handles the OAuth2 authorization code callback from identity_service
 */

import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, consumeReturnUrl } from '@shared/auth';

const OIDCCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { handleOIDCCallback, isAuthenticated, defaultReturnUrl } = useAuth();
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(true);
  const processedRef = useRef(false);

  useEffect(() => {
    // Prevent double processing in strict mode
    if (processedRef.current) return;
    processedRef.current = true;

    const processCallback = async () => {
      try {
        const code = searchParams.get('code');
        const errorParam = searchParams.get('error');
        const errorDescription = searchParams.get('error_description');

        // Handle OAuth error response
        if (errorParam) {
          setError(errorDescription || errorParam);
          setProcessing(false);
          return;
        }

        // Validate required parameters
        if (!code) {
          setError('Missing authorization code');
          setProcessing(false);
          return;
        }

        // Exchange code for tokens via AuthContext
        const result = await handleOIDCCallback();

        if (result.success) {
          navigate(consumeReturnUrl(defaultReturnUrl), { replace: true });
        } else {
          setError(result.error || 'Authentication failed');
          setProcessing(false);
        }
      } catch (err) {
        console.error('[OIDCCallback] Error:', err);
        setError(err.message || 'An unexpected error occurred');
        setProcessing(false);
      }
    };

    processCallback();
  }, [searchParams, handleOIDCCallback, navigate]);

  // If already authenticated, redirect
  useEffect(() => {
    if (isAuthenticated && !processing) {
      navigate(consumeReturnUrl(defaultReturnUrl), { replace: true });
    }
  }, [isAuthenticated, processing, navigate]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full space-y-8 p-8">
          <div className="text-center">
            <div className="mx-auto h-12 w-12 text-red-500">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
              Authentication Failed
            </h2>
            <p className="mt-2 text-sm text-gray-600">{error}</p>
            <button
              onClick={() => navigate('/auth', { replace: true })}
              className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full space-y-8 p-8">
        <div className="text-center">
          <div className="animate-spin mx-auto h-12 w-12 text-indigo-600">
            <svg fill="none" viewBox="0 0 24 24">
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          </div>
          <h2 className="mt-6 text-xl font-semibold text-gray-900">
            Completing sign in...
          </h2>
          <p className="mt-2 text-sm text-gray-500">
            Please wait while we verify your credentials
          </p>
        </div>
      </div>
    </div>
  );
};

export default OIDCCallback;
